import { useEffect, useState } from "react";
import { Card, Textarea } from "@swot/base-components";
import { 
  MessageSquare, 
  Zap, 
  X, 
  ShieldCheck, 
  AlertCircle,
  Loader2
} from "lucide-react";
import type { ComplaintAllowedActionDto, ComplaintWorkflowActionRequest } from "../../../types/complaint.types.js";
import { CmtsActionFooter } from "../CmtsActionFooter.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

export function CmtsWorkflowActionDialog({ 
  action, 
  rowVersion, 
  busy, 
  error, 
  onCancel, 
  onConfirm 
}: {
  readonly action: ComplaintAllowedActionDto | null;
  readonly rowVersion?: string | null | undefined;
  readonly busy?: boolean;
  readonly error?: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: (payload: ComplaintWorkflowActionRequest) => void | Promise<void>;
}) {
  const [remarks, setRemarks] = useState("");

  useEffect(() => { 
    setRemarks(""); 
  }, [action?.actionCode]);

  if (!action) return null;

  const safeError = error ? normalizeUserSafeApiError(error).userMessage : null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="w-full max-w-lg rounded-[2.5rem] bg-white p-2 shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-8 duration-500">
        <div className="overflow-hidden rounded-[2.2rem] bg-white border border-slate-50">
          
          {/* Dialog Header */}
          <div className="relative overflow-hidden bg-slate-900 p-8 text-white">
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-indigo-500/20 blur-2xl" />
            <div className="relative z-10 flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-indigo-400">
                  <ShieldCheck className="h-4 w-4" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Secure Workflow</span>
                </div>
                <h2 className="text-2xl font-black tracking-tight">
                  Confirm {action.displayLabel}
                </h2>
              </div>
              <button 
                onClick={onCancel}
                className="rounded-full bg-white/10 p-2 text-white/60 transition-colors hover:bg-white/20 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Dialog Content */}
          <div className="p-8 space-y-6">
            <div className="flex items-start gap-3 rounded-2xl bg-indigo-50/50 p-4 border border-indigo-100/50">
              <Info className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
              <p className="text-xs font-medium text-indigo-700 leading-relaxed">
                The backend workflow action endpoint validates permission, workflow, SoD, concurrency and idempotency before execution.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <MessageSquare className="h-4 w-4 text-slate-400" />
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                  {action.reasonMessage ?? "Action Remarks"}
                </span>
              </div>
              <div className="relative group">
                <Textarea 
                  value={remarks} 
                  onChange={(event) => setRemarks(event.currentTarget.value)} 
                  rows={4}
                  className="w-full rounded-2xl border-slate-100 bg-slate-50/50 p-4 text-slate-700 transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500/10 placeholder:text-slate-300"
                  placeholder="Enter detailed notes for this action..."
                />
              </div>
            </div>

            {safeError && (
              <div className="flex items-center gap-2 rounded-xl bg-rose-50 p-3 text-rose-600 border border-rose-100 animate-in shake duration-500">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span className="text-xs font-bold">{safeError}</span>
              </div>
            )}
          </div>

          {/* Dialog Footer */}
          <div className="bg-slate-50/50 p-6 flex items-center justify-end gap-3 border-t border-slate-100">
            <button
              onClick={onCancel}
              disabled={busy}
              className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm({ 
                reason: remarks || undefined, 
                comment: remarks || undefined, 
                expectedRowVersion: rowVersion ?? undefined 
              })}
              disabled={busy}
              className="relative flex items-center gap-2 overflow-hidden rounded-xl bg-indigo-600 px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-200 transition-all hover:bg-indigo-700 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-70 disabled:hover:translate-y-0"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  Execute Action
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component for info icon since it wasn't imported from lucide-react in the main list
function Info({ className }: { className?: string }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
    </svg>
   );
}
